import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Settings, Check, Search, X } from 'lucide-react';
import { useToast } from './ui/Toast';
import { useTranslatedToast } from '../utils/translatedToast';

/**
 * Compact environment selector for the title bar: custom dropdown for quick
 * switching + an entry that opens the Environments management modal.
 * Styling uses .env-picker* rules in index.css — this project does not ship a full Tailwind JIT sheet.
 */
const EnvironmentPicker = ({
  environments = [],
  selectedEnvironment,
  onSelectedEnvironmentChange,
  onOpenEnvironmentsModal
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);
  const { showToast } = useToast();
  const toastT = useTranslatedToast(showToast);

  useEffect(() => {
    if (!open) { setSearchQuery(''); return; }
    const id = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
      const active = listRef.current?.querySelector('[data-active="true"]');
      if (active) active.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const filteredEnvironments = searchQuery.trim()
    ? environments.filter((env) =>
        (env.name || '').toLowerCase().includes(searchQuery.trim().toLowerCase())
      )
    : environments;

  const selectEnvironment = useCallback((env) => {
    onSelectedEnvironmentChange(env);
    setOpen(false);
    if (env) {
      toastT('environment.switchedTo', 'info', { name: env.name });
    } else {
      toastT('environment.noneSelected', 'info');
    }
  }, [onSelectedEnvironmentChange, toastT]);

  const label = selectedEnvironment?.name || t('common.noEnvironment');

  return (
    <div ref={containerRef} className="env-picker flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="env-picker__trigger h-7 rounded-md border border-gray-200 bg-white text-xs font-medium text-gray-800 shadow-sm hover:bg-gray-50 transition-colors"
        title={t('titlebar.activeEnvironment')}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <ChevronDown
          size={14}
          className={`env-picker__chevron text-gray-500 ${open ? 'env-picker__chevron--open' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <div
          className="env-picker__menu rounded-lg border border-gray-200 bg-white shadow-lg"
          role="listbox"
        >
          <div className="env-picker__search">
            <Search size={13} className="env-picker__search-icon" aria-hidden />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('environment.searchPlaceholder', 'Search environments…')}
              className="tab-dropdown-search-input min-w-0 flex-1 bg-transparent border-none p-0 focus:outline-none"
              autoComplete="off"
            />
            {searchQuery && (
              <button
                type="button"
                className="env-picker__search-clear"
                onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
                onMouseDown={(e) => e.preventDefault()}
                aria-label={t('sidebar.clearSearch')}
              >
                <X size={12} />
              </button>
            )}
          </div>

          <div className="env-picker__list" ref={listRef}>
            {!searchQuery && (
            <button
              type="button"
              role="option"
              aria-selected={!selectedEnvironment}
              onClick={() => selectEnvironment(null)}
              className={`env-picker__row ${!selectedEnvironment ? 'env-picker__row--active' : ''}`}
              style={{ marginTop: 4 }}
            >
              {!selectedEnvironment ? (
                <Check size={12} className="shrink-icon text-gray-700" />
              ) : (
                <span className="env-picker__row-spacer" aria-hidden />
              )}
              <span className="min-w-0 flex-1 truncate">{t('common.noEnvironment')}</span>
            </button>
            )}

            {filteredEnvironments.map((env) => {
              const active = selectedEnvironment?.id === env.id;
              return (
                <button
                  key={env.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => selectEnvironment(env)}
                  className={`env-picker__row ${active ? 'env-picker__row--active' : ''}`}
                  data-active={active ? 'true' : 'false'}
                >
                  {active ? (
                    <Check size={12} className="shrink-icon text-gray-700" />
                  ) : (
                    <span className="env-picker__row-spacer" aria-hidden />
                  )}
                  <span className="min-w-0 flex-1 truncate">{env.name}</span>
                </button>
              );
            })}
          </div>

          <div className="env-picker__footer">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenEnvironmentsModal?.();
              }}
              className="env-picker__row"
            >
              <Settings size={12} className="shrink-icon" />
              <span className="truncate">{t('environment.manageMenu')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvironmentPicker;
